import { type ChangeEvent, type FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiUrl, type EntityRegistrationPayload } from "../../config/api";
import { countryOptions } from "../../lib/regionOptions";
import { normaliseSubdivision, subdivisionSuggestions } from "../../lib/subdivisions";
import type { EntityRegistration } from "../../services/companies/generated/model";
import "./AddEntityModal.css";

interface CorporationTypeOption {
  value: string;
  label: string;
}

interface AddEntityModalProps {
  isOpen: boolean;
  onClose: () => void;
  editEntity?: EntityRegistration | null;
  onSave: (data: EntityRegistrationPayload) => void;
  isSubmitting?: boolean;
  errorMessage?: string | null;
}

const emptyForm: EntityRegistrationPayload = {
  country: "",
  subdivision: "",
  registrationNumber: "",
  corporationType: "",
  registeredDate: "",
};

const AddEntityModal = ({
  isOpen,
  onClose,
  editEntity = null,
  onSave,
  isSubmitting = false,
  errorMessage = null,
}: AddEntityModalProps) => {
  const { t } = useTranslation(["company", "common"]);
  const [form, setForm] = useState<EntityRegistrationPayload>(emptyForm);
  const [corpTypeOptions, setCorpTypeOptions] = useState<CorporationTypeOption[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    if (editEntity) {
      setForm({
        country: editEntity.country ?? "",
        subdivision: editEntity.subdivision ?? "",
        registrationNumber: editEntity.registrationNumber ?? "",
        corporationType: editEntity.corporationType ?? "",
        registeredDate: editEntity.registeredDate ? editEntity.registeredDate.slice(0, 10) : "",
      });
    } else {
      setForm(emptyForm);
    }
  }, [isOpen, editEntity]);

  // Pull suggested corporation types whenever the country changes. The list
  // is curated server-side so the user picks from familiar options instead of
  // free-typing inconsistent variants ("LLC" vs "Limited" vs "limited liability").
  useEffect(() => {
    if (!isOpen) return;
    const ctrl = new AbortController();
    const url = apiUrl(
      `/api/v1/companies/corporationTypes/${form.country ? `?country=${encodeURIComponent(form.country)}` : ""}`,
    );
    fetch(url, { credentials: "include", signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((data: { results?: CorporationTypeOption[] }) => {
        setCorpTypeOptions(data.results ?? []);
      })
      .catch((err) => {
        if (err?.name !== "AbortError") setCorpTypeOptions([]);
      });
    return () => ctrl.abort();
  }, [isOpen, form.country]);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!form.registeredDate) return;
    onSave({ ...form, subdivision: normaliseSubdivision(form.subdivision) });
  };

  if (!isOpen) return null;

  const isEdit = Boolean(editEntity?.id);
  const corpTypeSuggestions = corpTypeOptions
    .filter((o) => o.value !== "Other")
    .slice(0, 3)
    .map((o) => o.value)
    .join(", ");

  return (
    <div className="modal-overlay entity-modal-overlay" onClick={onClose}>
      <div className="modal-content entity-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header entity-modal-header">
          <h2 className="modal-title">{isEdit ? t("addEntity.editTitle") : t("addEntity.addTitle")}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label={t("common:actions.close")}>
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <form className="entity-modal-form" onSubmit={handleSubmit}>
          <div className="entity-modal-field">
            <label className="entity-modal-label" htmlFor="entity-country">
              {t("addEntity.country")}
            </label>
            <select
              id="entity-country"
              name="country"
              className="entity-modal-input"
              value={form.country}
              onChange={handleChange}
              required
            >
              <option value="">{t("addEntity.selectCountry")}</option>
              {countryOptions().map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="entity-modal-field">
            <label className="entity-modal-label" htmlFor="entity-subdivision">
              {t("addEntity.subdivision")}
            </label>
            <input
              id="entity-subdivision"
              name="subdivision"
              type="text"
              className="entity-modal-input"
              placeholder={t("addEntity.subdivisionPlaceholder", {
                example: subdivisionSuggestions(form.country)[0] ?? "CA-QC",
              })}
              list="entity-subdivision-suggestions"
              value={form.subdivision}
              onChange={handleChange}
            />
            <datalist id="entity-subdivision-suggestions">
              {subdivisionSuggestions(form.country).map((code) => (
                <option key={code} value={code} />
              ))}
            </datalist>
            <p className="entity-modal-hint">{t("addEntity.subdivisionHint")}</p>
          </div>
          <div className="entity-modal-field">
            <label className="entity-modal-label" htmlFor="entity-registrationNumber">
              {t("addEntity.registrationNumber")}
            </label>
            <input
              id="entity-registrationNumber"
              name="registrationNumber"
              type="text"
              className="entity-modal-input"
              placeholder={t("addEntity.registrationNumberPlaceholder")}
              value={form.registrationNumber}
              onChange={handleChange}
              required
            />
          </div>
          <div className="entity-modal-field">
            <label className="entity-modal-label" htmlFor="entity-corporationType">
              {t("addEntity.corporationType")}
            </label>
            <input
              id="entity-corporationType"
              name="corporationType"
              type="text"
              className="entity-modal-input"
              placeholder={
                corpTypeOptions.length > 0
                  ? t("addEntity.corporationTypeSuggestions", { suggestions: corpTypeSuggestions })
                  : t("addEntity.corporationTypePlaceholder")
              }
              list="entity-corporationType-suggestions"
              value={form.corporationType}
              onChange={handleChange}
              required
            />
            {corpTypeOptions.length > 0 && (
              <datalist id="entity-corporationType-suggestions">
                {corpTypeOptions
                  .filter((o) => o.value !== "Other")
                  .map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
              </datalist>
            )}
          </div>
          <div className="entity-modal-field">
            <label className="entity-modal-label" htmlFor="entity-registeredDate">
              {t("addEntity.registeredDate")}
            </label>
            <div className="entity-modal-date-wrap">
              <input
                id="entity-registeredDate"
                name="registeredDate"
                type="date"
                className="entity-modal-input entity-modal-input--date"
                value={form.registeredDate}
                onChange={handleChange}
                required
              />
              <i className="fa-regular fa-calendar entity-modal-date-icon" aria-hidden />
            </div>
          </div>

          {errorMessage && (
            <p className="entity-modal-error" role="alert">
              {errorMessage}
            </p>
          )}

          <div className="entity-modal-footer">
            <button
              type="button"
              className="entity-modal-btn entity-modal-btn--cancel"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t("common:actions.cancel")}
            </button>
            <button type="submit" className="entity-modal-btn entity-modal-btn--primary" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin" aria-hidden />
                  {isEdit ? t("addEntity.updating") : t("addEntity.adding")}
                </>
              ) : isEdit ? (
                t("addEntity.updateSubmit")
              ) : (
                t("addEntity.addSubmit")
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEntityModal;
